/** @file Deform offsets of a mesh: its rest vertices, full-length expansion, trimming to the moved span. */
import type { MeshAtt } from '@pose2d/format/types';

type Offsets = { start: number; values: number[] };

/** Helpers between the stored sparse form `{ start, values } | null` and a full 2-per-vertex array. */
export const meshOffsets = {
  /** Rest positions deform offsets are relative to: rigid vertices, or skinned bind vertices. */
  rest(mesh: MeshAtt): number[] {
    return mesh.geometry.kind === 'rigid' ? mesh.geometry.vertices : mesh.geometry.bindVertices;
  },
  /** Full offsets array of `size` numbers (null = all zero). */
  expand(offsets: Offsets | null, size: number): number[] {
    const out = new Array<number>(size).fill(0);
    if (!offsets) return out;
    const { start, values } = offsets;
    values.forEach((v, i) => {
      out[start + i] = v;
    });
    return out;
  },
  /** Sparse form from the first to the last non-zero vertex (start even); null when every value is zero. */
  trim(full: readonly number[]): Offsets | null {
    const first = full.findIndex((v) => v !== 0);
    if (first < 0) return null;
    const last = full.findLastIndex((v) => v !== 0);
    const start = first - (first % 2);
    const end = last + 2 - (last % 2);
    return { start, values: full.slice(start, end) };
  },
};
